import {
  GUEST_CODE_CREATABLE_TARGET_TYPES,
  GUEST_CODE_LABEL_MAX_LENGTH,
  GUEST_CODE_PLACEMENTS,
  GUEST_CODE_SOCIAL_PLATFORMS,
  GUEST_CODE_STATUSES,
  GUEST_CODE_URL_MAX_LENGTH,
  GUEST_WIFI_SECURITY,
  isHttpsUrl,
} from "@order-up/models";
import { z } from "zod";

/**
 * Zod schemas for guest codes — mirroring packages/models/src/guest_codes.
 * Vocabulary is built from the model tuples; nothing is redeclared.
 *
 * `GuestCodeTargetSchema`'s coverage of every `GUEST_CODE_TARGET_TYPES` entry
 * is proven by a test (see __tests__/GuestCode.test.ts), not by a compile-time
 * assertion here — an unused-variable assertion checks nothing at runtime and
 * would not fail if a union member were removed.
 */

export const GuestCodePlacementSchema = z.enum(GUEST_CODE_PLACEMENTS);
export const GuestCodeStatusSchema = z.enum(GUEST_CODE_STATUSES);

const httpsUrl = z
  .string()
  .max(GUEST_CODE_URL_MAX_LENGTH)
  .refine(isHttpsUrl, { message: "Must be an https:// link" });

/** Any stored target (includes `table`). */
export const GuestCodeTargetSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("website") }).strict(),
  z.object({ type: z.literal("menu") }).strict(),
  z.object({ type: z.literal("ordering") }).strict(),
  z.object({ type: z.literal("table"), tableGuid: z.string().min(1).max(128) }).strict(),
  z.object({ type: z.literal("loyaltyJoin") }).strict(),
  z.object({ type: z.literal("googleReview") }).strict(),
  z.object({ type: z.literal("social"), socialPlatform: z.enum(GUEST_CODE_SOCIAL_PLATFORMS) }).strict(),
  z.object({ type: z.literal("wifi") }).strict(),
  z.object({ type: z.literal("itemFeedback"), feedbackGuid: z.string().min(1).max(128) }).strict(),
  z.object({ type: z.literal("customUrl"), url: httpsUrl }).strict(),
]);

/** Targets an operator may create or switch to from the hub (no `table`). */
export const CreatableGuestCodeTargetSchema = GuestCodeTargetSchema.refine(
  (target) => (GUEST_CODE_CREATABLE_TARGET_TYPES as readonly string[]).includes(target.type),
  { message: "Table codes are created from the floor plan" },
);

export const GuestCodeSchema = z
  .object({
    guid: z.string().min(1),
    tenantDocId: z.string().min(1),
    restaurantDocId: z.string().min(1),
    token: z.string().min(20).max(128),
    label: z.string().min(1).max(GUEST_CODE_LABEL_MAX_LENGTH),
    placement: GuestCodePlacementSchema,
    target: GuestCodeTargetSchema,
    status: GuestCodeStatusSchema,
    scanCount: z.number().int().nonnegative(),
    replacedByGuid: z.string().min(1).optional(),
    createdAt: z.unknown().optional(),
    updatedAt: z.unknown().optional(),
    retiredAt: z.unknown().optional(),
    createdByUid: z.string().optional(),
    updatedByUid: z.string().optional(),
  })
  .strict();

/** What the management API sends: no token, ISO timestamps. */
export const GuestCodeWireSchema = GuestCodeSchema.omit({ token: true }).extend({
  createdAt: z.iso.datetime().optional(),
  updatedAt: z.iso.datetime().optional(),
  retiredAt: z.iso.datetime().optional(),
});

export const CreateGuestCodeInputSchema = z
  .object({
    label: z.string().trim().min(1).max(GUEST_CODE_LABEL_MAX_LENGTH),
    placement: GuestCodePlacementSchema,
    target: CreatableGuestCodeTargetSchema,
  })
  .strict();

/**
 * `status` may only move to `retired` through a patch. Re-activation is not a
 * patch: a retired code's replacement is already printed, and two live codes
 * for one placement double-count. Rotation (`POST …/rotate`) is the only way a
 * placement gets a new live code.
 */
export const UpdateGuestCodeInputSchema = z
  .object({
    label: z.string().trim().min(1).max(GUEST_CODE_LABEL_MAX_LENGTH).optional(),
    placement: GuestCodePlacementSchema.optional(),
    target: CreatableGuestCodeTargetSchema.optional(),
    status: z.literal("retired").optional(),
  })
  .strict();

/** WPA/WPA2/WPA3-Personal passphrase minimum (IEEE 802.11i); 63 is the field ceiling. */
export const GUEST_WIFI_WPA_PASSWORD_MIN_LENGTH = 8;

/**
 * Guest wifi as printed into a `WIFI:` QR payload — so it must be JOINABLE,
 * not just well-formed: a WPA network with a short or empty password, or an
 * open network carrying a password, prints a code no phone can join.
 *
 * The rules are a `superRefine` on the object (zod 4 keeps that a ZodObject,
 * so `.shape`, `.optional()` and `.readonly()` still compose in the strict
 * restaurant settings schema). Their messages are operator copy — the
 * management Guest wifi card renders them as-is.
 */
export const GuestWifiSettingsSchema = z
  .object({
    ssid: z.string().trim().min(1).max(32),
    password: z.string().max(63),
    security: z.enum(GUEST_WIFI_SECURITY),
  })
  .strict()
  .superRefine((wifi, ctx) => {
    const issue = (message: string) =>
      ctx.addIssue({ code: "custom", path: ["password"], message });
    if (wifi.security === "nopass") {
      if (wifi.password !== "") issue("An open network (No password) can’t have a password.");
      return;
    }
    if (wifi.password === "") {
      issue("Enter the network password, or choose No password.");
      return;
    }
    if (wifi.security === "WPA" && wifi.password.length < GUEST_WIFI_WPA_PASSWORD_MIN_LENGTH) {
      issue(`A WPA password must be at least ${GUEST_WIFI_WPA_PASSWORD_MIN_LENGTH} characters.`);
    }
  });

export const OrderAttributionSchema = z
  .object({
    channel: z.literal("qr"),
    guestCodeGuid: z.string().min(1).max(128),
    placement: GuestCodePlacementSchema,
  })
  .strict();

export type GuestCodeWire = z.infer<typeof GuestCodeWireSchema>;
export type CreateGuestCodePayload = z.infer<typeof CreateGuestCodeInputSchema>;
export type UpdateGuestCodePayload = z.infer<typeof UpdateGuestCodeInputSchema>;
